import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {EEToolContextType} from "../types/EEToolContextType";
import {ProductQty} from "../types/ProductQty";
import {ProductQtyId} from "../types/ProductQtyId";
import {SaveEstimateRequest} from "../types/SaveEstimateRequest";
import {useParams} from "react-router-dom";
import {useGetEstimate} from "../api/get-estimate";

const EEToolContext = createContext<EEToolContextType>({} as EEToolContextType);
const useEEToolContext = () => useContext(EEToolContext);

function EEToolContextProvider(props: React.PropsWithChildren<unknown>) {
    const {quoteId} = useParams();
    const estimateResponse = useGetEstimate(quoteId ?? '0');
    const [quoteNumber, setQuoteNumber] = useState<string>('');
    const [dealerId, setDealerId] = useState<number>(0);
    const [estimateDate, setEstimateDate] = useState<Date>(new Date());
    const [drawData, setDrawData] = useState<string>('{}');
    const [note, setNote] = useState<string>('');
    const [salesAssoc, setSalesAssoc] = useState<string>('');
    const [salesAssocPhone, setSalesAssocPhone] = useState<string>('');
    const [salesAssocEmail, setSalesAssocEmail] = useState<string>('');
    const [salesAssocNumber, setSalesAssocNumber] = useState<string>('');
    const [ticketNumber, setTicketNumber] = useState<string>('');
    const [imageData, setImageData] = useState<string>('');
    const [itemizedCharges, setItemizedChargesInternal] = useState<ProductQtyId[]>([]);
    const [optionalProducts, setOptionalProducts] = useState<ProductQtyId[]>([]);
    const [prodQteGroups, setProdQteGroups] = useState<number[]>([]);
    const [materialProducts, setMaterialProducts] = useState<number[]>([]);
    const [counter, setCounter] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);
    const [is25Check, setIs25Check] = useState(false);
    const [retailPriceBucketID, setRetailPriceBucketID] = useState<number | undefined>(undefined);

    // Customer information
    const [customerName, setCustomerName] = useState<string>('');
    const [customerAddress1, setCustomerAddress1] = useState<string>('');
    const [customerAddress2, setCustomerAddress2] = useState<string>('');
    const [customerZip, setCustomerZip] = useState<string>('');
    const [customerState, setCustomerState] = useState<string>('');
    const [customerCity, setCustomerCity] = useState<string>('');
    const [customerCountry, setCustomerCountry] = useState<string>('');
    const [customerEmail, setCustomerEmail] = useState<string>('');
    const [customerPhone, setCustomerPhone] = useState<string>('');

    useEffect(() => {
        if (estimateResponse.data) {
            setDealerId(estimateResponse.data.dealerId);
            setEstimateDate(estimateResponse.data.quoteDate);
            setCustomerName(estimateResponse.data.customerInformation.name);
            setCustomerAddress1(estimateResponse.data.customerInformation.address1);
            setCustomerAddress2(estimateResponse.data.customerInformation.address2);
            setCustomerZip(estimateResponse.data.customerInformation.zip);
            setCustomerState(estimateResponse.data.customerInformation.state);
            setCustomerCity(estimateResponse.data.customerInformation.city);
            setCustomerCountry(estimateResponse.data.customerInformation.country);
            setCustomerEmail(estimateResponse.data.customerInformation.email);
            setCustomerPhone(estimateResponse.data.customerInformation.phone);
            setSalesAssoc(estimateResponse.data.salesAssoc);
            setSalesAssocNumber(estimateResponse.data.salesAssocNumber);
            setDrawData(estimateResponse.data.drawingData);
            setRetailPriceBucketID(estimateResponse.data.retailPriceBucketID);
            addOptionalProducts(estimateResponse.data.optionalItems);
            setIsLoaded(true);
        }
    }, [estimateResponse.data])

    function setItemizedCharges(items: ProductQty[]) {
        let result: ProductQtyId[] = [];
        for (let i = 0; i < items.length; i++) {
            result.push({...items[i], QuoteItemsID: counter + i});
        }
        setCounter(x => x + 1 + result.length);
        setItemizedChargesInternal(result);
    }

    function addOptionalProducts(items: ProductQty[]) {
        let result: ProductQtyId[] = [];
        for (let i = 0; i < items.length; i++) {
            result.push({...items[i], QuoteItemsID: -(counter + i)});
        }
        setCounter(x => x + 1 + result.length);
        setOptionalProducts(x => [...x, ...result]);
    }

    function deleteOptionalItem(quoteItemsID: number) {
        setOptionalProducts(x => x.filter(item => item.QuoteItemsID !== quoteItemsID));
    }

    function getSaveEstimateRequest() {
        let result: SaveEstimateRequest = {
            quoteId: quoteId,
            quoteNumber: quoteNumber,
            prodQteGrpId: prodQteGroups[0],
            estimateData: drawData,
            estimateItems: itemizedCharges,
            optionalItems: optionalProducts,
            estimateImage: imageData,
            dealerID: dealerId,
            note: note,
            salesAssoc: salesAssoc,
            salesAssocNumber: salesAssocNumber,
            ticketNumber: ticketNumber,
            retailPriceBucketId: retailPriceBucketID,
            customerInformation: {
                name: customerName,
                email: customerEmail,
                address1: customerAddress1,
                address2: customerAddress2,
                state: customerState,
                zip: customerZip,
                city: customerCity,
                phone: customerPhone,
                country: customerCountry
            }
        };

        return result;
    }

    return (
        <EEToolContext.Provider value={
            {
                dealerId,
                customerName,
                setCustomerName,
                customerAddress1,
                setCustomerAddress1,
                customerAddress2,
                setCustomerAddress2,
                customerState,
                setCustomerState,
                customerCity,
                setCustomerCity,
                customerCountry,
                setCustomerCountry,
                estimateDate,
                customerZip,
                setCustomerZip,
                customerPhone,
                setCustomerPhone,
                customerEmail,
                setCustomerEmail,
                setDealerId,
                drawData,
                setDrawData,
                imageData,
                setImageData,
                itemizedCharges,
                setItemizedCharges,
                optionalProducts,
                addOptionalProducts,
                prodQteGroups,
                setProdQteGroups,
                deleteOptionalItem,
                getSaveEstimateRequest,
                salesAssoc,
                setSalesAssoc,
                salesAssocPhone,
                setSalesAssocPhone,
                salesAssocEmail,
                setSalesAssocEmail,
                salesAssocNumber,
                setSalesAssocNumber,
                isLoading: (quoteId !== undefined) && estimateResponse.isLoading,
                isLoaded,
                materialProducts,
                setMaterialProducts,
                quoteId,
                quoteNumber,
                setQuoteNumber,
                is25Check,
                setIs25Check,
                ticketNumber,
                setTicketNumber,
                retailPriceBucketID,
                setRetailPriceBucketID
            }
        }
                                {...props}
        />);
}

export {EEToolContextProvider, useEEToolContext}
